import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getFormFields } from "../api/databaseApi";
import { logger } from "helpers/logger";

// formKey -> raw field rows, fetched once per app load (memory only, so config edits show after a reload).
const loadedForms = new Map();

/**
 * The fields of an app form (tbl_form_field_configs, e.g. "AddUser") for the application of the
 * caller's token, as EnhancedConfirmationDialog fields. There is deliberately no fallback template:
 * the backend validates against the same rows and refuses a form that has none, so a form that
 * isn't configured is reported (notConfigured) instead of drawn.
 *
 * Each field keeps its config's storage / usernameSource, so the page can tell e.g. the roles
 * field (storage "roles") apart and give it its options.
 *
 * @param {string} formKey - e.g. "AddUser"
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - false: don't fetch yet (e.g. until the dialog opens)
 * @returns {{ fields: Array, loading: boolean, error: string|null, notConfigured: boolean, reload: Function }}
 */
export function useFormFields(formKey, { enabled = true } = {}) {
	const [t] = useTranslation();
	const [rows, setRows] = useState(() => loadedForms.get(formKey) ?? null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const load = useCallback(
		async (signal) => {
			setLoading(true);
			setError(null);
			try {
				const response = await getFormFields(formKey, signal);
				const data = Array.isArray(response?.data) ? response.data : [];
				loadedForms.set(formKey, data);
				setRows(data);
			} catch (e) {
				if (e?.name === "AbortError") return;
				logger.error(`Failed to load the fields of form ${formKey}:`, e);
				setError(e?.message || "Failed to load the form");
			} finally {
				if (!signal?.aborted) setLoading(false);
			}
		},
		[formKey]
	);

	useEffect(() => {
		if (!enabled || loadedForms.has(formKey)) return undefined;
		const controller = new AbortController();
		load(controller.signal);
		return () => controller.abort();
	}, [enabled, formKey, load]);

	const reload = useCallback(() => {
		loadedForms.delete(formKey);
		load();
	}, [formKey, load]);

	const fields = useMemo(
		() =>
			(rows || []).map((row) => {
				const c = row.config || {};
				const type = c.type === "select" || c.type === "dropdown" ? c.type : "text";
				return {
					name: row.fieldKey,
					label: t(c.label || row.fieldKey),
					type,
					required: c.required === true,
					placeholder: c.placeholder ? t(c.placeholder) : "",
					regex: c.regex || undefined,
					regexErrorMessage: c.regexErrorMessage ? t(c.regexErrorMessage) : undefined,
					maxLength: Number.isInteger(c.maxLength) && c.maxLength > 0 ? c.maxLength : undefined,
					numericOnly: c.numericOnly === true,
					inputMode: c.numericOnly === true ? "numeric" : "text",
					pattern: c.numericOnly === true ? "[0-9]*" : undefined,
					multiple: c.multiple === true,
					options: Array.isArray(c.options) ? c.options.map((o) => ({ label: t(o.label ?? o.value), value: o.value })) : [],
					disabled: c.editable === false,
					storage: c.storage,
					target: c.target,
					usernameSource: c.usernameSource === true,
				};
			}),
		[rows, t]
	);

	return {
		fields,
		loading,
		error,
		// Loaded, and the application has no rows for this form.
		notConfigured: rows !== null && rows.length === 0,
		reload,
	};
}
